import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { getVoting, postVotingSuggestionToUpcoming } from 'src/api/stoApi';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { AddPost } from 'src/components/assets/forum/AddPost';
import { AddVotingDialog } from 'src/components/assets/forum/AddVotingDialog';
import { ForumHeader } from 'src/components/assets/forum/ForumHeader';
import { ForumPost } from 'src/components/assets/forum/ForumPost';
import { ForumVoting } from 'src/components/assets/forum/ForumVoting';
import { ForumVotingDetail } from 'src/components/assets/forum/ForumVotingDetail';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useForumDetails } from 'src/hooks/useForumMessages';
import { IForumVotings } from 'src/models';
import { TAssetStage, TSecurityType } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { generalStore } from 'src/stores/GeneralStore';
import { isNullUndefinedOrEmpty } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useAsset } from '../../context-providers/AssetContext';

export const AssetForumSite = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const { asset } = useAsset();
  const [selectedVoting, setSelectedVoting] = useState<IForumVotings | null>();
  const [reload, setReload] = useState(false);
  const { forumMessages, isLoading: isLoadingForum } = useForumDetails(assetId ?? '', reload);

  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentFilter, setCurrentFilter] = useState<'all' | 'messages' | 'voting'>('all');
  const [showNewVotingDialog, setShowNewVotingDialog] = useState(false);

  const getMessages = (all: boolean) => {
    let sortedValues = forumMessages?.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    if (currentFilter === 'messages') {
      sortedValues = sortedValues?.filter((x) => !x.voting);
    } else if (currentFilter === 'voting') {
      sortedValues = sortedValues?.filter((x) => x.voting);
    }

    if (all) {
      return sortedValues;
    } else {
      return sortedValues?.slice(page * 10, page * 10 + 10);
    }
  };

  const forumVotingClicked = async (assetId: string, votingId: string) => {
    try {
      if (selectedVoting) {
        if (selectedVoting?.id === votingId) {
          setSelectedVoting(null);
        } else {
          const response = await getVoting(assetId, votingId);
          setSelectedVoting(response.data.data);
        }
      } else {
        const response = await getVoting(assetId, votingId);
        setSelectedVoting(response.data.data);
      }
    } catch (error) {
      generalStore.setError('Error while fetching voting details');
    }
  };

  const startVotingClicked = async () => {
    try {
      await postVotingSuggestionToUpcoming(selectedVoting?.id ?? '');
      if (assetId) {
        const voting: IForumVotings = await getVoting(assetId, selectedVoting?.id ?? '').then((res) => res.data.data);
        setSelectedVoting(voting);
      }
    } catch (error) {
      generalStore.setError('Error while starting voting');
    } finally {
      generalStore.setIsLoading(false);
    }
  };

  if (isLoadingForum && !forumMessages) {
    return null;
  }

  if (!asset) {
    navigate(Routes.ASSETS.EDIT.replace(':assetId', assetId ?? ''));
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <AddVotingDialog
        open={showNewVotingDialog}
        handleClose={() => setShowNewVotingDialog(false)}
        assetId={assetId ?? ''}
        username={assetId ?? ''}
        reload={() => setReload(!reload)}
      />
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset?.id ?? ''),
            label: (isNullUndefinedOrEmpty(asset?.descriptions?.title) ? asset?.id : asset?.descriptions?.title) ?? '',
          },
          {
            link: Routes.ASSETS.FORUM.replace(':assetId', asset?.id ?? ''),
            label: t('dashboard.breadcrumbs.forum'),
          },
        ]}
        headline={asset ? (isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions.title as string)) : ''}
      >
        {asset && (
          <AssetDetailsLinkList
            assetId={asset.id}
            active={'forum'}
            isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
            isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
            isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
            isKESt={
              asset?.investmentDetails?.securityType === TSecurityType.participationRightDebtCapital ||
              asset?.investmentDetails?.securityType === TSecurityType.participationRightDebtCapitalPartialSale
            }
            securityType={asset?.investmentDetails?.securityType}
            useSerialNumber={asset.useSerialNumber}
          />
        )}
      </SiteHeader>
      <ForumHeader
        currentFilter={currentFilter}
        total={getMessages(true)?.length ?? 0}
        page={page}
        onPageChange={setPage}
        setCurrentFilter={(filter) => {
          setCurrentFilter(filter);
          setSelectedVoting(null);
          setPage(0);
        }}
        createNewVoting={() => setShowNewVotingDialog(true)}
      />
      <div
        style={{
          display: 'flex',
          flexGrow: 1,
        }}
      >
        {/* left */}
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              height: '1px',
              flexGrow: 1,
              overflow: 'scroll',
              paddingRight: 12,
            }}
          >
            <div
              style={{
                width: '100%',
              }}
            >
              {currentFilter !== 'voting' && <AddPost reload={() => setReload(!reload)} assetId={assetId ?? ''} username={assetId ?? ''} />}
              {getMessages(false)?.map((x, index) => {
                if (x.voting) {
                  return (
                    <ForumVoting
                      item={x}
                      key={index}
                      onClick={() => {
                        if (x.voting) {
                          forumVotingClicked(x.assetId, x.voting);
                        }
                      }}
                    />
                  );
                } else {
                  return <ForumPost item={x} key={index} />;
                }
              })}
            </div>
          </div>
        </div>

        {/* right */}
        <div
          style={{
            display: 'flex',
          }}
        >
          {selectedVoting && (
            <div style={{ paddingLeft: 12, minWidth: 500 }}>
              <ForumVotingDetail voting={selectedVoting} startVotingCallback={startVotingClicked} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
